/**
 * The non-mode `misc` tail — the PORTABLE pages (RO-3-12 / #44305).
 *
 * "Misc" is RO-3-01's grouping (#44294 §4) for the 15 pages genuinely outside the
 * mode system — `login`, `join`, `notifications`, `help/*`, `apps/*`, `dev`,
 * `skeletons`, `o/[uid]`, `transparency`, and the bare workspace home. Its own note
 * is explicit that the grouping exists "for port-planning purposes only, it does
 * not imply they should become one", and this file honours that: it holds the two
 * pages that are portable TODAY and share nothing but that fact. `/transparency`
 * lives in its own file because it carries a contract.
 *
 * ── Two routes out of fifteen, with the measurement that says why ──
 *
 * RO-3-01 classified by WRAPPER LOC. Re-measuring the DELEGATE closure (RO-3-00's
 * `rollover-shared-render-graph.mjs` resolution + taint rules, so the figures are
 * comparable) inverts the picture almost completely:
 *
 *   page                      wrapper → closure LOC / files / server-tainted
 *   skeletons                     34 →        0 /   0 / 0   ← ported below
 *   join                          26 →      247 /   1 / 0   ← ported below
 *   transparency                  33 →      115 /   1 / 0   ← ./transparency.tsx
 *   dev                          173 →      228 /   4 / 1   ← singletons.tsx (#44577)
 *   login                         26 →      719 /   4 / 1   ← singletons.tsx (#44577)
 *   help/*                    22–101 →  ~7,981 /   2 / 1   ← #44577 + #44460
 *   apps · apps/[slug]         18/28 →    9,648 /   7 / 3   ← #44577 + #44464
 *   excellent/now                 86 →   10,271 /   7 / 4   ← singletons.tsx (#44577)
 *   notifications                254 →   34,738 / 113 / 63  ← singletons.tsx (#44577)
 *   page.tsx (workspace home)     58 →   43,248 / 154 / 93  ← singletons.tsx (#44577)
 *   o/[uid]                       29 →   60,922 / 222 / 121  ← BLOCKED RO-3-00 (#44459)
 *
 * Deferrals closed by later items are noted above. The one that is not merely
 * deferred but BLOCKED is `o/[uid]`: its entire body is `<ObjectResolver/>` from
 * `app/_modes/object-resolver.tsx`, which value-imports `@excellent/core/server`
 * twice and sits in RO-3-00's un-portable set (§4 — 33 files / 9,051 LOC that
 * "cannot be ported at all" until contracts exist). It is RO-3-00's territory and
 * this item did not touch it.
 */

import * as React from "react";
import { z } from "zod";
import { createRoute } from "@tanstack/react-router";
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
  PageNav,
  SkeletonExamples,
} from "@excellent/ui";
import { LayoutGrid } from "@excellent/ui/icons";
import { apiCall, ApiError } from "@excellent/ui/api";
import { sync } from "@excellent/ui/api/contracts/sync";
import { rendezvous } from "@excellent/ui/api/contracts/rendezvous";
import { RouterLink as Link } from "@excellent/ui/router";
import { rootRoute } from "./__root";
import { routeSearchSchema } from "../shell/search";

// ── /skeletons ──────────────────────────────────────────────────────────────
// Port of `app/skeletons/page.tsx`. The cleanest port in the section: zero
// delegates, zero server reach, every import already on `@excellent/ui`. Body is
// verbatim apart from the Next `metadata` export, which has no counterpart in this
// shell yet (see `transparency.tsx`'s note on the missing document-title seam —
// the same gap, filed once).

function Skeletons() {
  return (
    <>
      <PageNav
        section={{ label: "Design", href: "/skeletons" }}
        tabs={[
          {
            href: "/skeletons",
            label: "Skeletons",
            icon: <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.75} />,
          },
        ]}
      />
      <div className="px-6 pt-5">
        <header className="flex flex-col gap-1 pb-2">
          <h2 className="type-heading text-foreground">Skeleton patterns</h2>
          <p className="type-body text-muted-foreground">
            Canonical loading states. Every isLoading branch in the monorepo
            should match one of these shapes. If you need a new shape, add it
            to <code className="text-foreground">packages/ui/src/blocks/skeleton-examples.tsx</code> first.
          </p>
        </header>
        <SkeletonExamples />
      </div>
    </>
  );
}

export const skeletonsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/skeletons",
  component: Skeletons,
});

// ── /join ───────────────────────────────────────────────────────────────────
// Port of `app/join/page.tsx` + `app/join/_client.tsx` (TEAM-UX-PRD §5.4). A
// teammate opens the invite link on their own fresh install; this writes the node
// config + stashes the invite, then tells them to restart as a replica.
//
// The client half ported verbatim — it was ALREADY `"use client"`, already drove
// every internal call through `apiCall` against a contract, and both contracts it
// needs are mounted on the Hono host (`sync.join` and `rendezvous.apply`, both in
// `server/routes/integrations.ts`). Verified rather than assumed: RO-3-00 §6 records
// nine already-portable components whose contracts are NOT mounted, so "it
// compiles" is not evidence the calls resolve.
//
// `?bundle=` is declared through `routeSearchSchema`, which refuses to compile if a
// route claims a shell-wide key. Checked the other direction too, because query
// params are a SHELL-WIDE namespace (`?from=` is back-link provenance, `?task=` is
// the object peek): `bundle` has exactly one reader in the tree — this page — so
// there is no collision to inherit.

const joinSearchSchema = routeSearchSchema({
  /** Legacy invite payload. Route-local: `/join` is its only reader, and the
   *  preferred secure path carries its material in the URL FRAGMENT instead. */
  bundle: z.string().optional(),
});

const REASONS: Record<string, string> = {
  "invalid-bundle": "This invite link is malformed or corrupted. Ask for a fresh one.",
  "invalid-or-expired": "This invite has already been used or has expired. Ask for a fresh one.",
  "no-primary-url": "This invite has no primary URL — ask the owner to re-issue it from the node that provisioned the primary.",
  "owner-only": "This workspace already has members — joining is only available on a fresh single-player install.",
};

// MUST match LOOKUP_INFO in packages/core/src/fleet/handoff.ts (rendezvousKey).
// The joiner derives the rendezvous lookup from the code IN THE BROWSER so the
// raw code never leaves this machine — preserving the zero-knowledge property
// (the broker only ever sees the hash).
const LOOKUP_INFO = "excellent-invite-handoff-lookup-v1";

async function rendezvousLookup(code: string): Promise<string> {
  const enc = new TextEncoder();
  const info = enc.encode(LOOKUP_INFO);
  const codeBytes = enc.encode(code);
  const data = new Uint8Array(info.length + 1 + codeBytes.length);
  data.set(info, 0);
  data[info.length] = 0; // domain-separation null byte
  data.set(codeBytes, info.length + 1);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

interface SecureParams {
  code: string;
  issuerKey: string;
  broker: string;
}

function parseSecureHash(hash: string): SecureParams | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return null;
  const p = new URLSearchParams(raw);
  const code = p.get("code");
  const issuerKey = p.get("k");
  const broker = p.get("r");
  if (!code || !issuerKey || !broker) return null;
  // Only allow http(s) brokers — the browser fetches this origin.
  try {
    const u = new URL(broker);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
  } catch {
    return null;
  }
  return { code, issuerKey, broker };
}

function JoinClient({ bundle }: { bundle: string | null }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Legacy (?bundle=) path.
  const [done, setDone] = React.useState<{ sourceCommand: string; hasToken: boolean } | null>(null);
  // Secure (#code&k&r) path.
  const [secure, setSecure] = React.useState<SecureParams | null>(null);
  const [hashChecked, setHashChecked] = React.useState(false);
  const [secureDone, setSecureDone] = React.useState<{ hotFlipped: boolean; workspace: string | null } | null>(null);

  // The secure invite's material rides in the URL FRAGMENT, which never reaches a
  // server on either stack — so it is read in an effect here exactly as it was in
  // the Next client component. Not a `validateSearch` concern: TanStack parses the
  // query string, and a fragment is deliberately not part of it.
  React.useEffect(() => {
    setSecure(parseSecureHash(window.location.hash));
    setHashChecked(true);
  }, []);

  async function joinSecure(params: SecureParams) {
    setBusy(true);
    setError(null);
    try {
      const lookup = await rendezvousLookup(params.code);
      // External fetch to the broker (a different origin / host) — exempt from
      // the no-`fetch()` rail (third-party host, not an internal endpoint). The
      // broker returns the standard { ok, data } envelope with CORS allowed.
      const base = params.broker.replace(/\/+$/, "");
      // seam-ignore: Excellent peer rendezvous broker (external origin, not this node's API)
      const res = await fetch(`${base}/api/sync/rendezvous/claim?k=${encodeURIComponent(lookup)}`);
      const json = (await res.json().catch(() => null)) as
        | { ok: boolean; data?: { ciphertext: string | null } }
        | null;
      const ciphertext = json && json.ok ? json.data?.ciphertext ?? null : null;
      if (!ciphertext) {
        setError(REASONS["invalid-or-expired"] ?? "This invite has already been used or has expired. Ask for a fresh one.");
        return;
      }
      // Apply on OUR OWN server (same-origin): decrypt + authenticate + hot-flip.
      const r = await apiCall(rendezvous.apply, {
        body: { code: params.code, ciphertext, issuerKey: params.issuerKey },
      });
      if (!r.ok) {
        setError(REASONS[r.reason ?? ""] ?? r.reason ?? "Could not join.");
        return;
      }
      setSecureDone({ hotFlipped: r.hotFlipped, workspace: r.workspace });
    } catch (e) {
      setError(e instanceof ApiError ? e.error : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function join() {
    setBusy(true);
    setError(null);
    try {
      const r = await apiCall(sync.join, { body: { bundle: bundle as string } });
      if (!r.ok || !r.sourceCommand) {
        setError(REASONS[r.reason ?? ""] ?? r.reason ?? "Could not join.");
      } else {
        setDone({ sourceCommand: r.sourceCommand, hasToken: r.hasToken });
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.error : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // ---- Secure success ------------------------------------------------------
  if (secureDone) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            {secureDone.hotFlipped ? "Connected — you're in" : "Connected — restart to finish"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {secureDone.hotFlipped ? (
            <p className="type-body text-muted-foreground">
              This machine is now syncing with{" "}
              {secureDone.workspace ? <strong>{secureDone.workspace}</strong> : "the team"}.
              Register a passkey to finish joining — your invite applies on first
              sign-in.
            </p>
          ) : (
            <p className="type-body text-muted-foreground">
              Your sync config is saved. Restart Excellent, then register a
              passkey — your invite applies on first sign-in.
            </p>
          )}
          {/* `/login` is mounted by #44577 (`loginRoute` in singletons.tsx) —
              use the router seam so the SPA stays on the Vite host. */}
          <Button asChild>
            <Link href="/login">Register a passkey</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ---- Secure invite (preferred) ------------------------------------------
  if (hashChecked && secure) {
    return (
      <Card>
        <CardContent className="space-y-4 py-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <p className="type-body text-muted-foreground">
            Click to securely fetch this team's connection details and connect
            this machine. The invite link never carried the credentials — they're
            picked up once, encrypted, and verified against the team's key.
          </p>
          <Button onClick={() => joinSecure(secure)} disabled={busy}>
            {busy ? "Connecting…" : "Connect to the workspace"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ---- Legacy success ------------------------------------------------------
  if (done) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Almost there — restart to join</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="type-body text-muted-foreground">
            Node config written. Restart Excellent as a replica, then register a
            passkey — your invite is applied automatically on first sign-in.
          </p>
          <pre className="overflow-x-auto rounded-md bg-muted/40 p-3 type-subtext">{`${done.sourceCommand}\npnpm dev`}</pre>
          {!done.hasToken && (
            <Alert>
              <AlertDescription>
                This invite had no sync token. If the primary requires auth,
                ask the owner for a libSQL token and add it with{" "}
                <code className="font-mono">excellent-mcp sync setup</code>.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  }

  // ---- No invite -----------------------------------------------------------
  if (hashChecked && !secure && !bundle) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          No invite found in this link. Open the full link from your invite, or
          run <code className="font-mono">excellent-mcp sync join --invite &lt;code&gt;</code>.
        </AlertDescription>
      </Alert>
    );
  }

  // ---- Legacy bundle -------------------------------------------------------
  if (!hashChecked) return null; // brief: waiting to read the link fragment
  return (
    <Card>
      <CardContent className="space-y-4 py-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <p className="type-body text-muted-foreground">
          Click to connect this machine to the workspace. We'll write your local
          sync config; nothing is sent anywhere until you restart.
        </p>
        <Button onClick={join} disabled={busy}>
          {busy ? "Joining…" : "Join this workspace"}
        </Button>
      </CardContent>
    </Card>
  );
}

function Join() {
  const { bundle } = joinRoute.useSearch();
  return (
    <main className="mx-auto max-w-xl px-6 py-10">
      <PageHeader
        title="Join a workspace"
        description="You were invited to a shared Excellent workspace. This connects this machine to the team’s primary."
      />
      <div className="mt-6">
        <JoinClient bundle={bundle ?? null} />
      </div>
    </main>
  );
}

export const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/join",
  validateSearch: joinSearchSchema,
  component: Join,
});
